import { Component, ElementRef, input, output, ViewChild, effect, untracked, TemplateRef, model } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as docx from 'docx-preview';

@Component({
  selector: 'sia-file-preview-word',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatIconButton, MatProgressSpinnerModule],
  templateUrl: './file-preview-word.component.html',
  styleUrl: './file-preview-word.component.css',
  host: {
    class: 'block h-full w-full',
  },
})
export class FilePreviewWordComponent {
  fileBlob = input<Blob | null>(null);
  fullscreen = model<boolean>(false);
  actionsTemplate = input<TemplateRef<any>>();
  loaded = output<void>();

  @ViewChild('wordContainer') wordContainer?: ElementRef<HTMLElement>;

  isLoading = true;
  error: string | null = null;
  zoomLevel = 1;

  constructor() {
    effect(() => {
      const blob = this.fileBlob();
      if (blob) {
        this.renderWordFile(blob);
      }
    });

    // Re-render when fullscreen toggles, because Angular destroys/recreates the #wordContainer
    effect(() => {
      this.fullscreen();
      const blob = untracked(() => this.fileBlob());
      if (blob) {
        this.renderWordFile(blob);
      }
    });
  }

  async renderWordFile(blob: Blob) {
    this.isLoading = true;
    this.error = null;
    try {
      // Small timeout to ensure ViewChild is resolved if it's newly rendered
      setTimeout(async () => {
        if (!this.wordContainer?.nativeElement) return;

        try {
          await docx.renderAsync(blob, this.wordContainer.nativeElement, this.wordContainer.nativeElement, {
            className: 'docx', // Default
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            ignoreLastRenderedPageBreak: false,
            experimental: true,
            trimXmlDeclaration: true,
            useBase64URL: false,
          });

          this.isLoading = false;
          // Emit loaded event after DOM has updated so mark.js can index it
          setTimeout(() => {
            this.loaded.emit();
          }, 100);
        } catch (renderError: any) {
          console.error('Error rendering DOCX:', renderError);
          this.error = 'Impossibile visualizzare il file Word. Il formato potrebbe non essere supportato.';
          this.isLoading = false;
        }
      });
    } catch (e: any) {
      console.error('Error in docx preparation:', e);
      this.error = 'Errore durante il caricamento del documento.';
      this.isLoading = false;
    }
  }

  zoomIn() {
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
  }

  zoomOut() {
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
  }

  resetZoom() {
    this.zoomLevel = 1;
  }

  onWheel(event: WheelEvent) {
    if (event.ctrlKey) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }
  }
}
